import AddIcon from "@mui/icons-material/AddRounded";
import RefreshIcon from "@mui/icons-material/CachedRounded";
import { Box, Button, Container, Stack } from "@mui/material";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import RecordCounter from "@entrilia/shared/components/filters/RecordCounter";
import SearchField from "@entrilia/shared/components/inputs/SearchField";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../api/adminApi";
import type { ClientUserGroupBase } from "../../../../api/types/userManagementTypes";
import useClientContext from "../../../../context/useClientContext";
import GeneralPageHeader from "../../../common/GeneralPageHeader";
import DeleteUserGroupDialog from "./DeleteUserGroupDialog";
import UserGroupsGrid from "./UserGroupsGrid";

const UserGroupsPage = () => {
  const { hasAnyPermission } = useClientContext();
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState("");
  const [userGroupToDelete, setUserGroupToDelete] = useState<ClientUserGroupBase>();

  const [userGroups, fetchError, { fetch, setData }] = useFetch(adminApi.getClientUserGroups);

  const handleDeleteUserGroup = useCallback((row: ClientUserGroupBase) => {
    setUserGroupToDelete(row);
  }, []);

  const handleUserGroupDeleted = useCallback(
    (id: string) => {
      setData((prev) => prev?.filter((group) => group.id !== id) ?? []);
    },
    [setData]
  );

  if (fetchError) {
    logError(fetchError, "[UserGroupsPage] getClientUserGroups");
    return <DataLoadingFailed />;
  }

  const handleSearch = (value: string) => {
    setSearchTerm(value.trim().toLocaleLowerCase());
  };

  const handleCreateNewGroup = () => {
    navigate("new");
  };

  const hasPermissionToManage = hasAnyPermission(["ManageOrganizationUsers"]);

  const loading = userGroups === undefined;

  const filteredUserGroups = (userGroups ?? []).filter((template) =>
    template.name.toLocaleLowerCase().includes(searchTerm)
  );

  return (
    <>
      <GeneralPageHeader title="User Groups">
        {hasPermissionToManage && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateNewGroup}>
            New Group
          </Button>
        )}
      </GeneralPageHeader>
      <Container maxWidth="lg" sx={{ pt: 2.5, flex: 1 }}>
        <Box sx={{ width: "100%", display: "flex", justifyContent: "space-between", mb: 2 }}>
          <RecordCounter records={filteredUserGroups.length} totalRecords={userGroups?.length ?? 0} hide={loading} />
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <SearchField debounceTimeMs={300} onSearch={handleSearch} />
            <Button
              variant="outlined"
              color="secondary"
              disabled={loading}
              startIcon={<RefreshIcon />}
              onClick={() => fetch()}
            >
              Refresh
            </Button>
          </Stack>
        </Box>

        <UserGroupsGrid rows={filteredUserGroups} loading={loading} onDeleteUserGroup={handleDeleteUserGroup} />
      </Container>
      {userGroupToDelete !== undefined && (
        <DeleteUserGroupDialog
          userGroup={userGroupToDelete}
          onClose={() => setUserGroupToDelete(undefined)}
          onDeleted={handleUserGroupDeleted}
        />
      )}
    </>
  );
};

export default UserGroupsPage;
